import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import {
  FaEye, FaEllipsisV, FaCheckCircle, FaUserSlash,
  FaBan, FaSync, FaEnvelope, FaMapMarkerAlt,
  FaTh, FaList, FaBuilding, FaStar, FaArrowLeft,
  FaBed, FaPhone, FaCalendarAlt
} from 'react-icons/fa';
import AppContext from '../../../context/AppContext';
import '../../../assets/css/admin/adminUsersManagement/usertable.css';

import VendorBookingDetailPanel from './VendorBookingDetailPanel';

const getAvatarColor = (id = '', name = '') => {
  const colors = ['#2C5F7C', '#4A9E8E', '#FFB800', '#DC3545', '#17A2B8', '#6C757D', '#28A745'];
  const str = (id + name).toString();
  const hash = str.split('').reduce((a, b) => ((a << 5) - a) + b.charCodeAt(0), 0);
  return colors[Math.abs(hash) % colors.length];
};

const getInitials = (name = '') =>
  name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || '??';

const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric'
  });
};

const isHotelActive = (vendor) => vendor?.jjhotelregistration?.hotel_status === 'active';

const VendorCard = ({ vendor, onView, onApprove, onSuspend }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const hotel = vendor.jjhotelregistration || {};
  const status = isHotelActive(vendor) ? 'approved' : 'pending';

  return (
    <div className="adm-vendor-card">
      <div className="adm-vc-header">
        <div className="adm-vc-avatar-placeholder">{getInitials(vendor.name)}</div>
        <div className={`adm-vc-status adm-vc-status-${status}`}>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </div>
      </div>
      <div className="adm-vc-body">
        <h3 className="adm-vc-name">{vendor.name}</h3>
        <p className="adm-vc-owner">Owner: {hotel.owner_name}</p>
        <div className="adm-vc-info">
          <div className="adm-vc-info-item"><FaMapMarkerAlt className="adm-vc-icon" /> {hotel.address}</div>
          <div className="adm-vc-info-item"><FaStar className="adm-vc-icon" style={{ color: '#FFB800' }} /> {hotel.stars || 0} Stars</div>
        </div>
        <div className="adm-vc-stats">
          <div className="adm-vc-stat">
            <span className="adm-vc-stat-value">{hotel.total_rooms || 0}</span>
            <span className="adm-vc-stat-label">Rooms</span>
          </div>
          <div className="adm-vc-stat">
            <span className="adm-vc-stat-value">{vendor.jjbookings_count ?? vendor.jjbookings?.length ?? 0}</span>
            <span className="adm-vc-stat-label">Bookings</span>
          </div>
        </div>
      </div>
      <div className="adm-vc-footer">
        <button className="adm-vc-btn adm-vc-btn-view" onClick={() => onView(vendor)}>
          <FaEye /> View
        </button>
        <div style={{ position: 'relative' }}>
          <button
            className="ut-menu-btn"
            onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen); }}
          >
            <FaEllipsisV />
          </button>
          {menuOpen && (
            <div className="ut-dropdown-menu" style={{ position: 'absolute', right: 0, bottom: '110%', zIndex: 999 }}>
              {!isHotelActive(vendor) ? (
                <button
                  className="ut-dd-item ut-dd-success"
                  onClick={() => { setMenuOpen(false); onApprove(vendor); }}
                >
                  <FaCheckCircle /> Approve
                </button>
              ) : (
                <button
                  className="ut-dd-item ut-dd-danger"
                  onClick={() => { setMenuOpen(false); onSuspend(vendor); }}
                >
                  <FaBan /> Suspend
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const VendorDetail = ({ vendorId, onBack, baseUrl, authToken }) => {
  const [currentVendor, setCurrentVendor] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, type: '', text: '' });
  const [tab, setTab] = useState('overview');
  const [selectedBookingId, setSelectedBookingId] = useState(null);

  const showToast = (type, text) => {
    setToast({ show: true, type, text });
    setTimeout(() => setToast({ show: false, type: '', text: '' }), 3000);
  };

  useEffect(() => {
    setPageLoading(true);
    axios
      .get(`${baseUrl}/admin/vendor/${vendorId}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        if (res.data?.success) setCurrentVendor(res.data.data);
      })
      .catch(() => showToast('error', 'Vendor detail load nahi ho saka.'))
      .finally(() => setPageLoading(false));
  }, [vendorId, baseUrl, authToken]);

  const hotel = currentVendor?.jjhotelregistration || {};
  const bookings = currentVendor?.jjbookings || [];
  const rooms = hotel?.jjrooms || [];

  let totalRevenue = 0;
  bookings.forEach(b => {
    const amt = parseFloat(b?.Jjpayment?.total_amount || b?.jjpayment?.total_amount || 0);
    if (!isNaN(amt)) totalRevenue += amt;
  });

  const handleApprove = () => {
    setLoading(true);
    const fd = new FormData();
    fd.append('hotel_id', hotel.id);
    fd.append('status', 'active');
    axios.post(`${baseUrl}/admin/hotel/update-status`, fd, {
      headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${authToken}` }
    })
      .then(res => {
        if (res.data.success) {
          setCurrentVendor(prev => ({
            ...prev,
            jjhotelregistration: { ...prev.jjhotelregistration, hotel_status: 'active' }
          }));
          showToast('success', 'Hotel activated successfully!');
        } else {
          showToast('error', res.data.message || 'Failed to activate');
        }
      })
      .catch(() => showToast('error', 'Failed to activate'))
      .finally(() => setLoading(false));
  };

  const handleSuspend = () => {
    setLoading(true);
    const fd = new FormData();
    fd.append('hotel_id', hotel.id);
    fd.append('status', 'inactive');
    axios.post(`${baseUrl}/admin/hotel/update-status`, fd, {
      headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${authToken}` }
    })
      .then(res => {
        if (res.data.success) {
          setCurrentVendor(prev => ({
            ...prev,
            jjhotelregistration: { ...prev.jjhotelregistration, hotel_status: 'inactive' }
          }));
          showToast('success', 'Hotel deactivated.');
        } else {
          showToast('error', res.data.message || 'Failed to deactivate');
        }
      })
      .catch(() => showToast('error', 'Failed to deactivate'))
      .finally(() => setLoading(false));
  };

  if (pageLoading) {
    return (
      <div className="vds-container" style={{ textAlign: 'center', padding: '4rem' }}>
        <FaSync className="ut-spin" /> Vendor detail load ho raha hai…
      </div>
    );
  }

  if (!currentVendor) {
    return (
      <div className="vds-container">
        <button className="vds-back-btn" onClick={onBack}><FaArrowLeft /> Back</button>
        <p style={{ marginTop: '1rem' }}>Vendor not found.</p>
      </div>
    );
  }

  const hotelActive = hotel?.hotel_status === 'active';
  const statusCls = hotelActive ? 'vds-approved' : 'vds-pending';
  const statusLabel = hotelActive ? 'Approved' : 'Pending';

  if (selectedBookingId) {
    return (
      <VendorBookingDetailPanel
        bookingId={selectedBookingId}
        onBack={() => setSelectedBookingId(null)}
        baseUrl={baseUrl}
        authToken={authToken}
      />
    );
  }

  return (
    <div className="vds-container">
      {toast.show && (
        <div className={`vds-toast vds-toast-${toast.type}`}>{toast.text}</div>
      )}

      <div className="vds-topbar">
        <button className="vds-back-btn" onClick={onBack}><FaArrowLeft /> Back</button>
        <h2 className="vds-page-title">Vendor Details</h2>
      </div>

      <div className="vds-hero">
        <div className="vds-avatar" style={{ background: hotelActive ? '#28A745' : '#FFB800' }}>
          {getInitials(currentVendor?.name)}
        </div>
        <div className="vds-hero-info">
          <h2 className="vds-name">{currentVendor?.name || ''}</h2>
          <div className="vds-badges">
            <span className={`vds-status-badge ${statusCls}`}>{statusLabel}</span>
            <span className="vds-role-badge">Vendor</span>
          </div>
          <p className="vds-id">ID: #{currentVendor?.id} | {currentVendor?.email || ''}</p>
        </div>
        <div className="vds-hero-actions">
          {hotelActive ? (
            <button className="vds-action-btn vds-btn-danger" onClick={handleSuspend} disabled={loading}>
              <FaBan /> {loading ? 'Processing...' : 'Suspend'}
            </button>
          ) : (
            <button className="vds-action-btn vds-btn-success" onClick={handleApprove} disabled={loading}>
              <FaCheckCircle /> {loading ? 'Processing...' : 'Approve'}
            </button>
          )}
        </div>
      </div>

      <div className="vds-tabs">
        {['overview', 'rooms', 'bookings'].map(t => (
          <button
            key={t}
            className={`vds-tab${tab === t ? ' vds-tab-active' : ''}`}
            onClick={() => setTab(t)}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
            {t === 'rooms' && ` (${rooms.length})`}
            {t === 'bookings' && ` (${bookings.length})`}
          </button>
        ))}
      </div>

      <div className="vds-tab-body">
        {tab === 'overview' && (
          <>
            <div className="vds-booking-grid" style={{ marginBottom: '1.25rem' }}>
              <div className="vds-booking-stat">
                <span className="vds-b-value">{hotel.total_rooms || 0}</span>
                <span className="vds-b-label">Rooms</span>
              </div>
              <div className="vds-booking-stat">
                <span className="vds-b-value">{bookings.length}</span>
                <span className="vds-b-label">Bookings</span>
              </div>
              <div className="vds-booking-stat">
                <span className="vds-b-value">{hotel.stars || 0}</span>
                <span className="vds-b-label">Stars</span>
              </div>
              <div className="vds-booking-stat">
                <span className="vds-b-value">PKR {totalRevenue.toLocaleString()}</span>
                <span className="vds-b-label">Revenue</span>
              </div>
            </div>
            <div className="vds-grid">
              <div className="vds-card">
                <h6 className="vds-card-title"><FaBuilding className="vds-card-icon" /> Hotel Info</h6>
                <div className="vds-field"><label>Hotel Name</label><p>{hotel.hotel_name}</p></div>
                <div className="vds-field"><label>Owner</label><p>{hotel.owner_name}</p></div>
                <div className="vds-field"><label>Phone</label><p>{hotel.phone}</p></div>
                <div className="vds-field"><label>Address</label><p>{hotel.address}</p></div>
              </div>
              <div className="vds-card">
                <h6 className="vds-card-title"><FaEnvelope className="vds-card-icon" /> Account</h6>
                <div className="vds-field"><label>Name</label><p>{currentVendor?.name}</p></div>
                <div className="vds-field"><label>Email</label><p>{currentVendor?.email}</p></div>
                <div className="vds-field"><label>Joined</label><p>{formatDate(currentVendor?.created_at)}</p></div>
              </div>
            </div>
          </>
        )}

        {tab === 'rooms' && (
          <div>
            {rooms.length > 0 ? (
              <div className="ut-scroll">
                <table className="ut-table">
                  <thead>
                    <tr>
                      <th>Room No</th><th>Type</th><th>Price</th><th>Capacity</th><th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rooms.map(room => (
                      <tr key={room.id}>
                        <td>#{room.room_number}</td>
                        <td>{room.room_type}</td>
                        <td>PKR {Number(room.price || 0).toLocaleString()}</td>
                        <td>{room.capacity}</td>
                        <td><span className="ut-badge ut-badge-active">{room.status || 'available'}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="vds-empty">No rooms registered.</p>
            )}
          </div>
        )}

        {tab === 'bookings' && (
          <div>
            {bookings.length > 0 ? (
              <>
                <p style={{ fontSize: '0.8rem', color: '#8a96a3', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <FaEye style={{ opacity: 0.6 }} />
                  Click on any row to view full booking details
                </p>
                <div className="ut-scroll">
                  <table className="ut-table">
                    <thead>
                      <tr>
                        <th>ID</th><th>Customer</th><th>Check-in</th><th>Check-out</th>
                        <th>Guests</th><th>Booking Status</th><th>Payment</th><th>Amount</th><th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.map(b => {
                        const payment = b?.Jjpayment || b?.jjpayment;
                        const customer = b?.Jjcustomer || b?.jjcustomer;
                        const bookingStatus = b.booking_status || 'confirmed';
                        const paymentStatus = b.payment_status || 'paid';

                        const bookingStatusColor = {
                          confirmed: 'ut-badge-active', pending: 'ut-badge-pending',
                          cancelled: 'ut-badge-inactive', completed: 'ut-badge-active',
                        }[bookingStatus] || 'ut-badge-pending';

                        const paymentStatusColor = {
                          paid: 'ut-badge-active', unpaid: 'ut-badge-inactive',
                          pending: 'ut-badge-pending', refunded: 'ut-badge-pending',
                        }[paymentStatus] || 'ut-badge-pending';

                        return (
                          <tr key={b.id} style={{ cursor: 'pointer' }} onClick={() => setSelectedBookingId(b.id)}>
                            <td style={{ fontFamily: 'monospace', fontWeight: 600, color: '#2C5F7C' }}>#{b.id}</td>
                            <td>
                              <div style={{ fontWeight: 600 }}>{customer?.cus_username || 'N/A'}</div>
                              <div style={{ fontSize: '0.75rem', color: '#8a96a3' }}>{customer?.cus_email || ''}</div>
                            </td>
                            <td>{formatDate(b.checkIn || b.check_in)}</td>
                            <td>{formatDate(b.checkOut || b.check_out)}</td>
                            <td>{b.number_of_guests}</td>
                            <td><span className={`ut-badge ${bookingStatusColor}`}>{bookingStatus}</span></td>
                            <td><span className={`ut-badge ${paymentStatusColor}`}>{paymentStatus}</span></td>
                            <td style={{ fontWeight: 700, color: '#28a745' }}>
                              PKR {parseFloat(payment?.total_amount || b.total_amount || 0).toLocaleString()}
                            </td>
                            <td>
                              <button
                                className="ut-btn-view"
                                onClick={(e) => { e.stopPropagation(); setSelectedBookingId(b.id); }}
                              >
                                <FaEye /> View
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <p className="vds-empty">No bookings found.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const UserTables = ({ role, onStatsUpdate, searchTerm = '', statusFilter = 'all' }) => {
const { baseUrl, authToken } = useContext(AppContext);
const [admins, setAdmins] = useState([]);
const [customers, setCustomers] = useState([]);
const [vendors, setVendors] = useState([]);
const [loading, setLoading] = useState(false);
const [message, setMessage] = useState({ type: '', text: '' });
const [openMenu, setOpenMenu] = useState(null);
const [vendorView, setVendorView] = useState('table');
const [detailVendor, setDetailVendor] = useState(null);
const isAdmin = localStorage.getItem('adminRole') === 'admin';

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const fetchAdmins = () => {
    setLoading(true);
    axios.get(`${baseUrl}/admin/all`, { headers: { Authorization: `Bearer ${authToken}` } })
      .then(res => {
        if (res.data.success) {
          const admins = res.data.admins || [];
          setAdmins(admins);
          if (onStatsUpdate) {
           onStatsUpdate({
             totaladmins: admins.length,
             active: admins.filter(a => a.admaccount_status === 'active').length,
             inactive: admins.filter(a => a.admaccount_status !== 'active').length,
           });
          }
        }
      })
      .catch(() => showMessage('error', 'Error loading admins'))
      .finally(() => setLoading(false));
  };

  const fetchCustomers = () => {
    setLoading(true);
    axios.get(`${baseUrl}/customer/all`, { headers: { Authorization: `Bearer ${authToken}` } })
      .then(res => {
        if (res.data.success) {
          const customers = res.data.customers || [];
         setCustomers(customers);
          if (onStatsUpdate) {
            onStatsUpdate({
            total: customers.length,
            active: customers.filter(c => c.cus_status === 'active').length,
            inactive: customers.filter(c => c.cus_status !== 'active').length,
});
          }
        }
      })
      .catch(() => showMessage('error', 'Error loading customers'))
      .finally(() => setLoading(false));
  };

  const fetchVendors = () => {
     if(onStatsUpdate){
    onStatsUpdate({
      total:0,
      active:0,
      pending:0,
      blocked:0
    });
  }

    setLoading(true);
    axios.get(`${baseUrl}/admin/view-vendor`, { headers: { Authorization: `Bearer ${authToken}` } })
      .then(res => {
        if (res.data.success) {
          const vendors = res.data.data || [];
         setVendors(vendors);
          if (onStatsUpdate) {
if (onStatsUpdate) {
  onStatsUpdate({
    total: vendors.length,
    active: vendors.filter(v => isHotelActive(v)).length,
    pending: vendors.filter(v => !isHotelActive(v)).length,
    blocked: 0
  });
}
}
        }
      })
      .catch(() => showMessage('error', 'Error loading vendors'))
      .finally(() => setLoading(false));
  };

const loadData = () => {
  if (!authToken) return;

  if (role === 'admins') fetchAdmins();
  if (role === 'vendors') fetchVendors();
  if (role === 'customers') fetchCustomers();
};

useEffect(() => { 
  setAdmins([]);
  setCustomers([]);
  setVendors([]);

  loadData();

}, [role, authToken]);


const rawUsers =
  role === 'admins' ? admins :
  role === 'vendors' ? vendors :
  customers;

const filtered = rawUsers.filter(u => {
    let name = '';
    let email = '';
    let hotelName = '';
    if (role === 'admins') {
      name = u.admusername || '';
      email = u.admemail || '';
    } else if (role === 'customers') {
      name = u.cus_username || '';
      email = u.cus_email || '';
    } else if (role === 'vendors') {
      name = u.name || '';
      email = u.email || '';
      hotelName = u.jjhotelregistration?.hotel_name || '';
    }
    const q = (searchTerm || '').toLowerCase();
    const matchSearch = !q ||
      name.toLowerCase().includes(q) ||
      email.toLowerCase().includes(q) ||
      hotelName.toLowerCase().includes(q);

    let userStatus = '';
    if (role === 'admins') userStatus = u.admaccount_status;
    else if (role === 'customers') userStatus = u.cus_status;
    else if (role === 'vendors') userStatus = isHotelActive(u) ? 'approved' : 'pending';
    const matchStatus = statusFilter === 'all' || userStatus === statusFilter;
    return matchSearch && matchStatus;
  });

  const handleAdminToggle = (admin) => {
    const newStatus = admin.admaccount_status === 'active' ? 'inactive' : 'active';
    setLoading(true);
    const fd = new FormData();
    fd.append('id', admin.id);
    fd.append('status', newStatus);
    axios.post(`${baseUrl}/admin/update-status`, fd, {
      headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${authToken}` }
    })
      .then(res => {
        if (res.data.success) {
          showMessage('success', `Admin ${newStatus} successfully!`);
          fetchAdmins();
        } else {
          showMessage('error', 'Failed to update status');
        }
      })
      .catch(() => showMessage('error', 'Failed to update status'))
      .finally(() => { setLoading(false); setOpenMenu(null); });
  };

  const handleVendorApprove = (vendor) => {
    setLoading(true);
    const fd = new FormData();
    fd.append('hotel_id', vendor.jjhotelregistration?.id);
    fd.append('status', 'active');
    axios.post(`${baseUrl}/admin/hotel/update-status`, fd, {
      headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${authToken}` }
    })
      .then(res => {
        if (res.data.success) {
          showMessage('success', 'Hotel activated!');
          fetchVendors();
        } else {
          showMessage('error', 'Failed to activate');
        }
      })
      .catch(() => showMessage('error', 'Failed to activate'))
      .finally(() => { setLoading(false); setOpenMenu(null); });
  };

  const handleVendorSuspend = (vendor) => {
    setLoading(true);
    const fd = new FormData();
    fd.append('hotel_id', vendor.jjhotelregistration?.id);
    fd.append('status', 'inactive');
    axios.post(`${baseUrl}/admin/hotel/update-status`, fd, {
      headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${authToken}` }
    })
      .then(res => {
        if (res.data.success) {
          showMessage('success', 'Hotel deactivated.');
          fetchVendors();
        } else {
          showMessage('error', 'Failed to deactivate');
        }
      })
      .catch(() => showMessage('error', 'Failed to deactivate'))
      .finally(() => { setLoading(false); setOpenMenu(null); });
  };

  if (!authToken || !isAdmin) {
    return (
      <div className="ut-wrapper">
        <div className="ut-message ut-message-error">Admin access only.</div>
      </div>
    );
  }

  
if (detailVendor) {
  return (
    <VendorDetail
      vendorId={detailVendor.id}   
      onBack={() => setDetailVendor(null)}
      baseUrl={baseUrl}
      authToken={authToken}
    />
  );
}

  if (role === 'vendors') {
    return (
      <div className="ut-wrapper">
        <div className="ut-toolbar">
          <span className="ut-count">Total Vendors: {filtered.length}</span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              className={`adm-ven-view-btn${vendorView === 'cards' ? ' adm-ven-view-btn-active' : ''}`}
              onClick={() => setVendorView('cards')}
            >
              <FaTh />
            </button>
            <button
              className={`adm-ven-view-btn${vendorView === 'table' ? ' adm-ven-view-btn-active' : ''}`}
              onClick={() => setVendorView('table')}
            >
              <FaList />
            </button>
            <button className="ut-refresh-btn" onClick={loadData} disabled={loading}>
              <FaSync className={loading ? 'ut-spin' : ''} /> Refresh
            </button>
          </div>
        </div>

        {message.text && (
          <div className={`ut-message ut-message-${message.type}`}>{message.text}</div>
        )}

        {loading && (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <FaSync className="ut-spin" /> Loading...
          </div>
        )}

        {!loading && vendorView === 'cards' && (
          filtered.length > 0 ? (
            <div className="adm-ven-cards-grid">
              {filtered.map(v => (
                <VendorCard
                  key={v.id}
                  vendor={v}
                  onView={setDetailVendor}
                  onApprove={handleVendorApprove}
                  onSuspend={handleVendorSuspend}
                />
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '3rem' }}>No vendors found.</div>
          )
        )}

        {!loading && vendorView === 'table' && (
          <div className="ut-scroll">
            <table className="ut-table">
              <thead>
                <tr>
                  <th>Vendor</th>
                  <th>Owner</th>
                  <th>Hotel</th>
                  <th>Location</th>
                  <th>Rooms</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(v => {
                  const hotel = v.jjhotelregistration || {};
                  const active = isHotelActive(v);
                  return (
                    <tr key={v.id}>
                      <td>
                        <div className="ut-name-cell">
                          <div className="ut-avatar" style={{ background: getAvatarColor(v.id, v.name) }}>
                            {getInitials(v.name)}
                          </div>
                          <div>
                            <div className="ut-username">{v.name}</div>
                            <div className="ut-email-cell">
                              <FaEnvelope className="ut-email-icon" /> {v.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>{hotel.owner_name}</td>
                      <td>{hotel.hotel_name}</td>
                      <td>
                        <div className="ut-email-cell">
                          <FaMapMarkerAlt className="ut-email-icon" /> {hotel.address}
                        </div>
                      </td>
                      <td>{hotel.total_rooms || 0}</td>
                      <td>
                        <span className={`ut-badge ${active ? 'ut-badge-active' : 'ut-badge-pending'}`}>
                          {active ? 'Approved' : 'Pending'}
                        </span>
                      </td>
                      <td>
                        <div className="ut-actions-cell">
                          <button className="ut-btn-view" onClick={() => setDetailVendor(v)}>
                            <FaEye /> View
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="ut-wrapper">
      <div className="ut-toolbar">
        <span className="ut-count">
          Total {role === 'admins' ? 'Admins' : 'Customers'}: {filtered.length}
        </span>
        <button className="ut-refresh-btn" onClick={loadData} disabled={loading}>
          <FaSync className={loading ? 'ut-spin' : ''} /> Refresh
        </button>
      </div>

      {message.text && (
        <div className={`ut-message ut-message-${message.type}`}>{message.text}</div>
      )}

      <div className="ut-scroll">
        <table className="ut-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Status</th>
              <th>Role</th>
              <th>Joined</th>
        

{role === 'admins' && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
         {loading && rawUsers.length === 0 ? (
              <tr>
                <td colSpan={role === 'admins' ? 6 : 5} className="ut-state-cell">
                  <FaSync className="ut-spin" /> Loading...
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={role === 'admins' ? 6 : 5} className="ut-state-cell">
               No records found.
              </td>
              </tr>
            ) : filtered.map(user => {
              const username = role === 'admins' ? user.admusername : user.cus_username;
              const email = role === 'admins' ? user.admemail : user.cus_email;
              const status = role === 'admins' ? user.admaccount_status : user.cus_status;
              return (
                <tr key={user.id}>
                  <td>
                    <div className="ut-name-cell">
                      <div
                        className="ut-avatar"
                        style={{ background: getAvatarColor(user.id, username) }}
                      >
                        {username?.charAt(0).toUpperCase()}
                      </div>
                      <div className="ut-username">{username}</div>
                    </div>
                  </td>
                  <td>
                    <div className="ut-email-cell">
                      <FaEnvelope className="ut-email-icon" /> {email}
                    </div>
                  </td>
                  <td>
                    <span className={`ut-badge ${status === 'active' ? 'ut-badge-active' : 'ut-badge-inactive'}`}>
                      {status}
                    </span>
                  </td>
                  <td>
                    <span className={`ut-role-badge ut-role-${role}`}>
                      {role === 'admins' ? 'Admin' : 'Customer'}
                    </span>
                  </td>
                  <td>{formatDate(user.created_at)}</td>
                  <td>
                   {role === 'admins' && (
  <td>
  <div className="ut-actions-cell">
    <div className="ut-dropdown" style={{ position: 'relative' }}>
      <button
        className="ut-menu-btn"
        onClick={() => setOpenMenu(openMenu === user.id ? null : user.id)}
      >
        <FaEllipsisV />
      </button>

      {openMenu === user.id && (
        <div className="ut-dropdown-menu">
          {status === 'inactive' ? (
            <button
              className="ut-dd-item ut-dd-success"
              onClick={() => handleAdminToggle(user)}
            >
              <FaCheckCircle /> Activate
            </button>
          ) : (
            <button
              className="ut-dd-item ut-dd-warning"
              onClick={() => handleAdminToggle(user)}
            >
              <FaUserSlash /> Deactivate
            </button>
          )}
        </div>
      )}
    </div>
  </div>
</td>
)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UserTables;